import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const ButtonContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const RunawayBtn = styled.button`
  position: relative;
  height: 2.5rem;
  width: 10rem;
  font-size: 1.1rem;
  border-radius: 5px;
  border: none;
  background-color:rgb(0, 94, 38);
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }

  &.running {
    position: fixed;
    z-index: 9999;
  }
`;

interface RunawayButtonProps {
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}

const RunawayButton: React.FC<RunawayButtonProps> = ({ disabled, children, onClick }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [hopCount, setHopCount] = useState(0);
  const [hasCompletedCycle, setHasCompletedCycle] = useState(false);
  const originalPosition = useRef({ top: 0, left: 0 });

  const getRandomNumber = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const resetButton = () => {
    if (!buttonRef.current) return;

    // Move back to original position
    buttonRef.current.style.top = `${originalPosition.current.top}px`;
    buttonRef.current.style.left = `${originalPosition.current.left}px`;

    // Reset to normal state
    setTimeout(() => {
      if (buttonRef.current) {
        buttonRef.current.classList.remove('running');
        buttonRef.current.style.position = 'relative';
        buttonRef.current.style.top = '';
        buttonRef.current.style.left = '';
        setIsRunning(false);
        setHopCount(0);
        setHasCompletedCycle(true); // Mark as completed permanently
      }
    }, 500);
  };

  const handleMouseOver = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Don't do anything if disabled, completed cycle, or at max hops
    if (disabled || hasCompletedCycle || !buttonRef.current || hopCount >= 10) return;
    
    if (!isRunning) {
      setIsRunning(true);
      buttonRef.current.classList.add('running');
      const rect = buttonRef.current.getBoundingClientRect();
      originalPosition.current = { top: rect.top, left: rect.left };
      buttonRef.current.style.top = `${rect.top}px`;
      buttonRef.current.style.left = `${rect.left}px`;
    }

    const newHopCount = hopCount + 1;
    setHopCount(newHopCount);

    if (newHopCount >= 10) {
      resetButton();
      return;
    }

    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    
    const buttonWidth = 160;
    const buttonHeight = 40;
    
    const padding = 20;
    const maxLeft = viewportWidth - buttonWidth - padding;
    const maxTop = viewportHeight - buttonHeight - padding;
    
    const top = getRandomNumber(padding, maxTop);
    const left = getRandomNumber(padding, maxLeft);

    buttonRef.current.style.top = `${top}px`;
    buttonRef.current.style.left = `${left}px`;
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && (!isRunning || hasCompletedCycle) && onClick) {
      event.stopPropagation();
      onClick();
    }
  };

  return (
    <ButtonContainer>
      <RunawayBtn
        ref={buttonRef}
        onMouseOver={handleMouseOver}
        onClick={handleClick}
        disabled={disabled}
      >
        {children}
      </RunawayBtn>
    </ButtonContainer>
  );
};

export default RunawayButton;
